import React, { useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { saveBlog, SaveBlogRequest } from "../../services/dashboard/SaveBlog";
import LoadingSpinner from "../utility/LoadingSpinner";
import CustomDropdown from "../app/CustomDropdown"; // Importar CustomDropdown
import "../../styles/dash/CreateOrEditChatBot.css"; // Reusing styles
import { useTranslation } from "react-i18next";

interface Props {
    show: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const CreateBlogModal: React.FC<Props> = ({ show, onClose, onSuccess }) => {
    const { i18n } = useTranslation();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [imageBase64, setImageBase64] = useState("");
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [seo, setSeo] = useState("");
    const [language, setLanguage] = useState(i18n.language || "es");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [animateOut, setAnimateOut] = useState(false);

    const URL_BACK_IMG = process.env.REACT_APP_API_IMGBLOG_URL;

    const languageOptions = [
        { value: "es", label: "Español" },
        { value: "en", label: "Inglés" },
    ];

    const handleClose = () => {
        setAnimateOut(true);
        setTimeout(() => {
            onClose();
            setAnimateOut(false);
            // Reset form
            setTitle("");
            setDescription("");
            setImageBase64("");
            setImagePreview(null);
            setSeo("");
            setLanguage(i18n.language || "es");
            setErrorMessage("");
        }, 300);
    };

    const handleSubmit = async () => {
        // Client-side validation
        if (!title.trim() || !description.trim() || !imageBase64.trim() || !seo.trim()) {
            setErrorMessage("Todos los campos, incluida la imagen, son obligatorios.");
            return;
        }

        setLoading(true);
        setErrorMessage("");

        try {
            const payload: SaveBlogRequest = {
                Title: title,
                Description: description,
                ImageName: imageBase64,
                SEO: seo,
                Language: language.toUpperCase(),
            };

            const response = await saveBlog(payload);
            if (response.status === 200) {
                onSuccess();
                handleClose();
            } else {
                setErrorMessage(response.message || "Ocurrió un error al crear el blog.");
            }

        } catch (error: any) {
            setErrorMessage(error.message || "Ocurrió un error al crear el blog.");
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                setErrorMessage("La imagen no puede superar los 5MB.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                setImageBase64(base64String);
                setImagePreview(base64String);
                setErrorMessage(""); 
            };
            reader.onerror = () => {
                setErrorMessage("Error al leer la imagen.");
            };
            reader.readAsDataURL(file);
        }
    };

    if (!show && !animateOut) return null;

    return (
        <Modal
            show={!animateOut}
            onHide={handleClose}
            centered
            size="lg"
            backdrop="static"
            contentClassName={`chatbot-form-modal ${animateOut ? 'modal-exit' : 'modal-enter'}`}
        >
            <Modal.Header closeButton closeVariant="white" className="border-0" />
            <Modal.Body className="chatbot-form-content">
                <h5 className="form-title text-center mb-4">Crear Nuevo Blog</h5>
                <Form>
                    {loading && <LoadingSpinner />}
                    <div className="form-grid">
                        <div className="form-group full-width"><label>Título</label><input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título del blog" /></div>
                        <div className="form-group full-width"><label>Descripción</label><textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descripción..." rows={5}></textarea></div>
                        <div className="form-group">
                            <label>Imagen de Portada</label>
                            <input
                                type="file"
                                id="imageUpload"
                                accept="image/png, image/jpeg, image/webp"
                                onChange={handleImageChange}
                                style={{ display: 'none' }}
                            />
                            <label htmlFor="imageUpload" className="file-upload-button">Seleccionar Imagen</label>
                            {imagePreview && (
                                <img src={imagePreview} alt="Vista previa" className="image-preview" />
                            )}
                        </div>
                        <div className="form-group full-width">
                            <label>Idioma</label>
                            <CustomDropdown
                                options={languageOptions}
                                defaultValue={language}
                                onChange={(value) => setLanguage(value)}
                            />
                        </div>
                        <div className="form-group"><label>SEO (Palabras clave)</label><input type="text" value={seo} onChange={(e) => setSeo(e.target.value)} placeholder="Palabras clave, etc." /></div>
                        <div className="form-group full-width">{errorMessage && <div className="error-message">{errorMessage}</div>}<button type="button" className="save-button" onClick={handleSubmit} disabled={loading}>{loading ? "CREANDO..." : "CREAR BLOG"}</button></div>
                        <div className="form-group full-width text-center"><button type="button" className="cancel-button" onClick={handleClose} disabled={loading}>Cerrar</button></div>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default CreateBlogModal;
